import type {
    BehaviorAveragesDto,
    EnvironmentalFactorsDto,
    OverviewResponseDto,
    RespirationDataPointDto,
    RespirationPercentagesDto,
    RespirationResponseDto,
    SwimSpeedDataPointDto,
    SwimSpeedResponseDto,
    TiltDataPointDto,
    TiltResponseDto,
} from '@/services/types';
import { toWallClock } from '@/utils/date';

import { toOptionalFiniteNumber } from '../shared/numbers';
import type { ApiWire } from '../shared/types';

import type {
    BehaviorAverages,
    EnvironmentalFactors,
    OverviewData,
    RespirationData,
    RespirationDataPoint,
    RespirationPercentages,
    SwimSpeedChartData,
    SwimSpeedDataPoint,
    TiltChartData,
    TiltDataPoint,
} from './types';

export function mapBehaviorAveragesToDomain(dto: BehaviorAveragesDto): BehaviorAverages {
    return {
        swimSpeed: dto.swimSpeed,
        numberOfSwimSpeedMeasurements: dto.numberOfSwimSpeedMeasurements,
        activeRespiration: dto.activeRespiration,
        numberOfRespirationMeasurements: dto.numberOfRespirationMeasurements,
        tilt: dto.tilt,
        numberOfTiltMeasurements: dto.numberOfTiltMeasurements,
    };
}

export function mapEnvironmentalFactorsToDomain(
    dto: ApiWire<EnvironmentalFactorsDto>
): EnvironmentalFactors {
    return {
        oxygenConcentration: dto.oxygenConcentration ?? undefined,
        oxygenSaturation: dto.oxygenSaturation ?? undefined,
        temperature: dto.temperature ?? undefined,
        depth: dto.depth ?? undefined,
        salinity: toOptionalFiniteNumber(dto.salinity),
        lastMeasurement: toWallClock(dto.lastMeasurement),
    };
}

export function mapRespirationPercentagesToDomain(
    dto: RespirationPercentagesDto
): RespirationPercentages {
    return {
        passive: dto.passive,
        active: dto.active,
        lastMeasurement: toWallClock(dto.lastMeasurement),
    };
}

export function mapOverviewResponseToDomain(dto: OverviewResponseDto): OverviewData {
    return {
        averages: mapBehaviorAveragesToDomain(dto.averages),
        environmentalFactors: mapEnvironmentalFactorsToDomain(dto.environmentalFactors),
        respirationPercentages: mapRespirationPercentagesToDomain(dto.respirationPercentages),
        isBioscopeRecentlyDeployed: dto.isBioscopeRecentlyDeployed ?? false,
    };
}

export function mapSwimSpeedDataPointToDomain(dto: SwimSpeedDataPointDto): SwimSpeedDataPoint {
    return {
        day: dto.day,
        activeSwimSpeed: dto.activeSwimSpeed,
        swimSpeed: dto.swimSpeed,
        temperature: dto.temperature,
        depth: dto.depth,
        oxygen: dto.oxygenSaturation,
        numberOfMeasurements: dto.numberOfMeasurements,
        numberOfActiveMeasurements: dto.numberOfActiveMeasurements,
    };
}

export function mapSwimSpeedResponseToDomain(dto: SwimSpeedResponseDto): SwimSpeedChartData {
    return {
        data: dto.data.map(mapSwimSpeedDataPointToDomain),
        lastMeasurement: toWallClock(dto.lastMeasurement),
    };
}

export function mapRespirationDataPointToDomain(
    dto: RespirationDataPointDto
): RespirationDataPoint {
    return {
        day: dto.day,
        respiration: dto.respiration,
        activePercent: dto.activePercent,
        passivePercent: dto.passivePercent,
        oxygen: dto.oxygenSaturation,
        swimSpeed: dto.swimSpeed,
        numberOfMeasurements: dto.numberOfMeasurements,
        certainRespiration: !dto.uncertainActiveFrequency,
        certainActivePercent: !dto.uncertainApprovedRatio,
    };
}

export function mapRespirationResponseToDomain(dto: RespirationResponseDto): RespirationData {
    return {
        data: dto.data.map(mapRespirationDataPointToDomain),
        lastMeasurement: toWallClock(dto.lastMeasurement),
    };
}

export function mapTiltDataPointToDomain(dto: TiltDataPointDto): TiltDataPoint {
    return {
        day: dto.day,
        tilt: dto.tilt,
        temperature: dto.temperature,
        depth: dto.depth,
        numberOfMeasurements: dto.numberOfMeasurements,
        oxygen: dto.oxygenSaturation,
    };
}

export function mapTiltResponseToDomain(dto: TiltResponseDto): TiltChartData {
    return {
        data: dto.data.map(mapTiltDataPointToDomain),
        lastMeasurement: toWallClock(dto.lastMeasurement),
    };
}
